import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { defer, finalize, Subject, takeUntil } from 'rxjs';
import { OkEgisServiceModelsDtoSlutopgoerelseDto } from 'src/app/api/models';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { SlutopgoerelseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/slutopgoerelse-dto';
import { SlutopgoerelseWithServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/slutopgoerelse-with-serviceaftale-dto';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';
import { SlutopgoerelseService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
    selector: 'app-slutopgoerelse-list',
    templateUrl: './slutopgoerelse-list.component.html',
    styleUrls: ['./slutopgoerelse-list.component.scss'],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('.25s ease-out', style({ transform: 'translateX(0%)', opacity: 1 })),
            ]),
            transition(':leave', [
                style({ transform: 'translateX(0%)', opacity: 1 }),
                animate('0.25s ease-in', style({ transform: 'translateX(100%)', opacity: 0 })),
            ]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlutopgoerelseListComponent implements OnInit {
  constructor(
    private router: Router,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private slutopgoerelseService: SlutopgoerelseService
  ) {}

  slutopgoerelser: SlutopgoerelseWithServiceaftaleDto[] = [];

  dataSource: MatTableDataSource<SlutopgoerelseWithServiceaftaleDto> = new MatTableDataSource<SlutopgoerelseWithServiceaftaleDto>();

  quickViewOpen: boolean = false;
  globalActiveRowID: string = '';
  selectedaftale: ServiceaftaleDto;

  sortActiveCol: string = 'CreatedDate';
  sortDir: SortDirection = 'desc';

  unsubscribe$ = new Subject<void>();

  displayedColumns: string[] = [
    'serviceaftale.serviceAbonnement.produkt.id',
    'EffectiveDate',
    'CreatedDate',
    'kundeNavn',
    'Remark',
    'ServiceaftaleState',
    'serviceaftale.okKundeAftaleNr',
    'adresse',
    'telefon',
  ];

  ngOnInit(): void {
    this.getSlutopgoerelser$.subscribe((slutopgoerelser: SlutopgoerelseWithServiceaftaleDto[]) => {
      this.slutopgoerelser = slutopgoerelser;
      this.sortData({ direction: this.sortDir, active: this.sortActiveCol });
    });
  }

  getSlutopgoerelser$ = defer(() => {
    this.loader.show();
    return this.slutopgoerelseService
      .getOverdueSlutopgoerelser({
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      );
  });

  onRowClick(row: any) {
    this.globalActiveRowID = row.id;
    this.quickViewOpen = true;
    this.selectedaftale = row.serviceaftale;
  }

  onRowDblClick(selectedaftale: ServiceaftaleDto) {
    this.router.navigate(['/slutopgoerelse', selectedaftale.okKundeAftaleNr]);
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  getStatusIcon(serviceAftale: ServiceaftaleDto) {
    if (serviceAftale.isActive) return 'checkmark';
    //Created-pending vs notactive
    return serviceAftale.serviceaftaleState.state == ServiceaftaleStateEnum.CreatedPending ? 'watch' : 'ban';
  }

  closeQuickView() {
    this.quickViewOpen = false;
    this.globalActiveRowID = '';
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      this.dataSource = new MatTableDataSource(this.slutopgoerelser);
      return;
    }

    this.dataSource = new MatTableDataSource(
      this.slutopgoerelser.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        switch (sort.active) {
          case 'EffectiveDate':
            return compare(a.effectiveDate, b.effectiveDate, isAsc);
          case 'CreatedDate':
            return compare(a.createdDate, b.createdDate, isAsc);
          case 'Remark':
            return compare(a.remark, b.remark, isAsc);
          case 'serviceaftale.okKundeAftaleNr':
            return compare(a.serviceaftale.okKundeAftaleNr, b.serviceaftale.okKundeAftaleNr, isAsc);
          default:
            return 0;
        }
      })
    );
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
